import { ReactElement } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react/polaris';
import { PackageData } from '@amzn/awsdev-docs-virtual-smiley-typescript-client';
import { EmptyState } from '../../ui/EmptyState';
import { getCanonicalPackageLink, getServiceLink } from '../utils';
import { getLocalizedPackageLocale } from '../packageLocale';

export type PackageMappingStatus =
  | 'Mapped'
  | 'Needs mapping'
  | 'Invalid service reference';

interface PackagesTableProps {
  data: PackageData[];
  loading: boolean;
  hasError: boolean;
}

export const getPackageMappingStatus = (
  packageData: PackageData
): PackageMappingStatus => {
  const serviceId = packageData.serviceId?.trim();

  if (!serviceId || serviceId.startsWith('temp_id')) {
    return 'Needs mapping';
  }

  return packageData.serviceDisplayName?.trim()
    ? 'Mapped'
    : 'Invalid service reference';
};

export const getPackageLocaleLabel = (packageData: PackageData): string =>
  getLocalizedPackageLocale(packageData) || 'Source';

export const comparePackageLocales = (
  firstPackage: PackageData,
  secondPackage: PackageData
): number =>
  getPackageLocaleLabel(firstPackage).localeCompare(
    getPackageLocaleLabel(secondPackage)
  );

const getMappingGuidance = (packageData: PackageData): string | undefined => {
  const status = getPackageMappingStatus(packageData);

  if (status === 'Invalid service reference') {
    return 'Update this package to reference an existing service.';
  }

  if (status === 'Needs mapping') {
    return packageData.serviceId?.trim().startsWith('temp_id')
      ? 'Replace the temporary service ID with the final service.'
      : 'Assign this package to a valid service.';
  }

  return undefined;
};

export const packageMatchesFilter = (
  packageData: PackageData,
  filteringText: string
): boolean => {
  const normalizedFilter = filteringText.toLowerCase();
  const searchableValues = [
    packageData.packageName,
    packageData.title,
    packageData.serviceDisplayName,
    packageData.serviceId,
    packageData.pathslug,
    getPackageLocaleLabel(packageData),
  ];

  return searchableValues.some((value) =>
    value?.toLowerCase().includes(normalizedFilter)
  );
};

const PackagesTable = ({
  data,
  loading,
  hasError,
}: PackagesTableProps): ReactElement => {
  const columnDefinitions = [
    {
      id: 'packageName',
      header: 'Package name',
      cell: (packageData: PackageData) =>
        getCanonicalPackageLink(packageData.packageName),
      sortingField: 'packageName',
    },
    {
      id: 'mappingStatus',
      header: 'Mapping status',
      cell: (packageData: PackageData) => {
        const status = getPackageMappingStatus(packageData);
        const guidance = getMappingGuidance(packageData);

        return (
          <SpaceBetween size='xxs'>
            <StatusIndicator
              type={
                status === 'Mapped'
                  ? 'success'
                  : status === 'Needs mapping'
                  ? 'pending'
                  : 'error'
              }
            >
              {status}
            </StatusIndicator>
            {guidance && <Box variant='small'>{guidance}</Box>}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'serviceDisplayName',
      header: 'Service name',
      cell: (packageData: PackageData) =>
        getPackageMappingStatus(packageData) === 'Mapped' &&
        packageData.serviceDisplayName &&
        packageData.serviceId
          ? getServiceLink(
              packageData.serviceDisplayName,
              packageData.serviceId
            )
          : '-',
      sortingField: 'serviceDisplayName',
    },
    {
      id: 'serviceId',
      header: 'Service ID',
      cell: (packageData: PackageData) => packageData.serviceId || '-',
      sortingField: 'serviceId',
    },
    {
      id: 'locale',
      header: 'Locale',
      cell: getPackageLocaleLabel,
      sortingComparator: comparePackageLocales,
    },
    {
      id: 'title',
      header: 'Title / pathslug',
      cell: (packageData: PackageData) => (
        <SpaceBetween size='xxs'>
          <Box>{packageData.title || '-'}</Box>
          {packageData.pathslug && (
            <Box variant='small'>{packageData.pathslug}</Box>
          )}
        </SpaceBetween>
      ),
      sortingField: 'title',
    },
  ];

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(data, {
    filtering: {
      empty: (
        <EmptyState
          title={
            hasError
              ? 'The list of packages could not be loaded'
              : 'No packages found'
          }
          subtitle={
            hasError
              ? 'Refresh the page to try loading package information again.'
              : 'A package that is not listed has not been ingested into Virtual Smiley.'
          }
          action={
            hasError ? (
              <Button onClick={() => location.reload()}>Refresh browser</Button>
            ) : null
          }
        />
      ),
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='No packages matched the specified search.'
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Clear search
            </Button>
          }
        />
      ),
      filteringFunction: packageMatchesFilter,
    },
    pagination: { pageSize: 20 },
    sorting: {
      defaultState: {
        sortingColumn: columnDefinitions[0],
        isDescending: false,
      },
    },
    selection: {},
  });
  const hasActiveFilter = filterProps.filteringText.trim().length > 0;

  return (
    <Box margin={{ top: 'l', bottom: 'xxl' }}>
      <Table
        {...collectionProps}
        header={
          <Header
            variant='h1'
            counter={
              hasActiveFilter && filteredItemsCount !== undefined
                ? `(${filteredItemsCount}/${data.length})`
                : `(${data.length})`
            }
            description='Search build-information records and check each package service mapping. Localized packages are view-only.'
          >
            Package information
          </Header>
        }
        trackBy='packageName'
        loading={loading}
        loadingText='Retrieving package data'
        columnDefinitions={columnDefinitions}
        items={items}
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter {...filterProps} filteringPlaceholder='Search packages' />
        }
        wrapLines
      />
    </Box>
  );
};

export default PackagesTable;
